import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getServiceRuleConfig,
  patchServiceRuleConfig,
  patchMerchantPercentage,
  createVoucher,
  listVouchers,
  revokeVoucher,
} from "services/apiMerchantPortal";

function errMsg(e, fallback) {
  if (typeof e === "string") return e;
  const d = e?.response?.data?.detail;
  if (d != null) {
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
      const parts = d.map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && item.msg != null) return String(item.msg);
        try {
          return JSON.stringify(item);
        } catch {
          return String(item);
        }
      });
      const joined = parts.filter(Boolean).join("; ");
      if (joined) return joined;
    } else if (typeof d === "object" && d.msg != null) {
      return String(d.msg);
    } else if (typeof d === "object") {
      try {
        return JSON.stringify(d);
      } catch {
        /* fall through */
      }
    }
  }
  const m = e?.message;
  if (typeof m === "string" && m) return m;
  return typeof fallback === "string" ? fallback : "Request failed";
}

const MerchantBusiness = () => {
  const [loading, setLoading] = useState(true);
  const [rule, setRule] = useState(null);
  const [pct, setPct] = useState("");
  const [numbersToSelect, setNumbersToSelect] = useState("");
  const [numberRange, setNumberRange] = useState("");
  const [dailyPriceNgn, setDailyPriceNgn] = useState("");
  const [freeDays, setFreeDays] = useState("");
  const [voucherAmount, setVoucherAmount] = useState("");
  const [voucherExpiryDays, setVoucherExpiryDays] = useState("");
  const [voucherNotes, setVoucherNotes] = useState("");
  const [vouchers, setVouchers] = useState({ content: [], totalElements: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const cfg = await getServiceRuleConfig();
      setRule(cfg);
      setNumbersToSelect(String(cfg.numbersToSelect ?? ""));
      setNumberRange(String(cfg.numberRange ?? ""));
      setDailyPriceNgn(String(cfg.dailyPriceNgn ?? ""));
      setFreeDays(String(cfg.freeDays ?? ""));
    } catch (e) {
      toast.error(errMsg(e, "Failed to load service rules"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const data = await axios.get("merchant/merchant-percentage");
        setPct(data !== undefined && data !== null ? String(data) : "");
      } catch {
        /* optional */
      }
    })();
    refreshVouchers();
  }, []);

  const refreshVouchers = async () => {
    try {
      const page = await listVouchers(0, 50);
      setVouchers(page);
    } catch {
      /* ignore */
    }
  };

  const saveRules = async (e) => {
    e.preventDefault();
    try {
      const body = {};
      if (numbersToSelect !== "") body.numbersToSelect = parseInt(numbersToSelect, 10);
      if (numberRange !== "") body.numberRange = parseInt(numberRange, 10);
      if (dailyPriceNgn !== "") body.dailyPriceNgn = parseFloat(dailyPriceNgn);
      if (freeDays !== "") body.freeDays = parseInt(freeDays, 10);
      const updated = await patchServiceRuleConfig(body);
      if (updated && typeof updated === "object") {
        setRule(updated);
      } else {
        await load();
      }
      toast.success("Lottery rules updated");
    } catch (e) {
      toast.error(errMsg(e, "Update failed"));
    }
  };

  const savePct = async (e) => {
    e.preventDefault();
    try {
      await patchMerchantPercentage(parseFloat(pct));
      toast.success("Merchant percentage updated");
    } catch (e) {
      toast.error(errMsg(e, "Update failed"));
    }
  };

  const issueVoucher = async (e) => {
    e.preventDefault();
    try {
      const body = { creditAmountNgn: parseFloat(voucherAmount) };
      if (voucherExpiryDays) body.expiresInDays = parseInt(voucherExpiryDays, 10);
      if (voucherNotes) body.notes = voucherNotes;
      const v = await createVoucher(body);
      toast.success(`Voucher created: ${v.code}`);
      setVoucherAmount("");
      setVoucherExpiryDays("");
      setVoucherNotes("");
      refreshVouchers();
    } catch (e) {
      toast.error(errMsg(e, "Failed to create voucher"));
    }
  };

  const onRevoke = async (id) => {
    try {
      await revokeVoucher(id);
      toast.success("Voucher revoked");
      refreshVouchers();
    } catch (e) {
      toast.error(errMsg(e, "Revoke failed"));
    }
  };

  if (loading && !rule) {
    return (
      <div className="p-6 text-center text-slate-600 dark:text-zink-200">Loading…</div>
    );
  }

  return (
    <div className="p-6 space-y-10 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-800 dark:text-zink-100">
        Merchant controls
      </h1>
      <p className="text-slate-600 dark:text-zink-300 text-sm">
        Set your revenue share, tune customer-facing lottery settings allowed for your service, and issue wallet vouchers.
        Compliance caps and shadow mode are still managed by Betaplay admin.
      </p>

      <section className="card p-6 shadow border border-slate-100 dark:border-zink-600 dark:bg-zink-800">
        <h2 className="text-lg font-medium mb-4 text-slate-800 dark:text-zink-100">
          Winning / revenue share (%)
        </h2>
        <form onSubmit={savePct} className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-sm mb-1 text-slate-600 dark:text-zink-300">Percentage (0–100)</label>
            <input
              className="form-input border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              type="number"
              step="0.01"
              min="0"
              max="100"
            />
          </div>
          <button
            type="submit"
            className="btn bg-custom-500 text-white px-4 py-2 rounded-md"
          >
            Save
          </button>
        </form>
      </section>

      <section className="card p-6 shadow border border-slate-100 dark:border-zink-600 dark:bg-zink-800">
        <h2 className="text-lg font-medium mb-2 text-slate-800 dark:text-zink-100">
          Lottery settings (your service)
        </h2>
        {rule && (
          <p className="text-xs text-slate-500 mb-4">
            Service: <strong>{rule.serviceId}</strong> — id #{rule.id}. Change shadow mode on API & VAS. Max-win caps stay admin-only.
          </p>
        )}
        <form onSubmit={saveRules} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Numbers to select</label>
            <input
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={numbersToSelect}
              onChange={(e) => setNumbersToSelect(e.target.value)}
              type="number"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Number range (max ball)</label>
            <input
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={numberRange}
              onChange={(e) => setNumberRange(e.target.value)}
              type="number"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Daily price (NGN)</label>
            <input
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={dailyPriceNgn}
              onChange={(e) => setDailyPriceNgn(e.target.value)}
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Free days</label>
            <input
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={freeDays}
              onChange={(e) => setFreeDays(e.target.value)}
              type="number"
            />
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="btn bg-custom-500 text-white px-4 py-2 rounded-md">
              Save lottery settings
            </button>
          </div>
        </form>
      </section>

      <section className="card p-6 shadow border border-slate-100 dark:border-zink-600 dark:bg-zink-800">
        <h2 className="text-lg font-medium mb-4 text-slate-800 dark:text-zink-100">Vouchers</h2>
        <form onSubmit={issueVoucher} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm mb-1">Credit amount (NGN)</label>
            <input
              required
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={voucherAmount}
              onChange={(e) => setVoucherAmount(e.target.value)}
              type="number"
              step="1"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Expires in days (optional)</label>
            <input
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={voucherExpiryDays}
              onChange={(e) => setVoucherExpiryDays(e.target.value)}
              type="number"
              min="1"
              max="365"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Notes (optional)</label>
            <input
              className="form-input w-full border-slate-200 dark:border-zink-500 dark:bg-zink-700 dark:text-zink-100"
              value={voucherNotes}
              onChange={(e) => setVoucherNotes(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="btn bg-custom-500 text-white px-4 py-2 rounded-md">
              Issue voucher
            </button>
          </div>
        </form>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-200 dark:border-zink-600">
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Expires</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {(vouchers.content || []).map((v) => (
                <tr key={v.id} className="border-b border-slate-100 dark:border-zink-700">
                  <td className="py-2 pr-4 font-mono">{v.code}</td>
                  <td className="py-2 pr-4">{v.creditAmountNgn}</td>
                  <td className="py-2 pr-4">{v.status}</td>
                  <td className="py-2 pr-4">{v.expiresAt || "—"}</td>
                  <td className="py-2 pr-4">
                    {v.status === "ACTIVE" && (
                      <button
                        type="button"
                        className="text-red-500 text-xs underline"
                        onClick={() => onRevoke(v.id)}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-4">
          Players redeem with <code className="bg-slate-100 dark:bg-zink-700 px-1 rounded">POST /api/v1/users/vouchers/redeem</code> using a <strong>ROLE_USER</strong> token.
        </p>
      </section>
    </div>
  );
};

export default MerchantBusiness;
